/**
 * The English dictionary — the single source of user-facing text.
 *
 * Every string the trader sees goes through `t()`, even in v1 when English is
 * the only language. Adding Urdu later then means writing `ur.ts` and flipping
 * `dir="rtl"`, rather than hunting through every component. Retrofitting this
 * is a week of tedious work; doing it from the start is free.
 *
 * The wording follows BR §17: the trader's words, not system words. Dalali,
 * not "commission". Mandi, not "market". Munshi, not "bookkeeper". Toori,
 * kuttar and wanda, not "fodder". Break-even, not "cost basis".
 */

import type {
  AnimalStatusCode,
  BreedCode,
  ExpenseCategoryCode,
  PaymentMethodCode,
  PlatformRoleCode,
  RoleCode,
  SexCode,
  TeethAgeCode,
} from './codes'

/**
 * Every stored code needs a label. Requiring these keys by construction means
 * a code added to `codes.ts` without a translation is a build error, not a
 * blank chip discovered by the trader.
 */
type CodeMessageKey =
  | `teeth.${TeethAgeCode}`
  | `sex.${SexCode}`
  | `breed.${BreedCode}`
  | `animalStatus.${AnimalStatusCode}`
  | `expenseCategory.${ExpenseCategoryCode}`
  | `paymentMethod.${PaymentMethodCode}`
  | `role.${RoleCode}`
  | `roleDescription.${RoleCode}`
  | `platformRole.${PlatformRoleCode}`
  | `platformRoleDescription.${PlatformRoleCode}`

export const en = {
  'app.name': 'Cattle Book',
  'app.tagline': 'Know what every animal really costs you',

  // --- navigation ---
  'nav.dashboard': 'Home',
  'nav.animals': 'Animals',
  'nav.expenses': 'Expenses',
  'nav.reports': 'Reports',
  'nav.more': 'More',
  'nav.admin': 'Admin',
  'nav.signOut': 'Sign Out',

  // --- setup ---
  'setup.title': 'Welcome',
  'setup.subtitle': 'Let us set up your account. This takes a minute and happens only once.',
  'setup.businessName': 'Business name',
  'setup.businessNameHint': 'Shown at the top of the app and on your reports.',
  'setup.yourName': 'Your name',
  'setup.username': 'Username',
  'setup.usernameHint': 'What you will type to sign in. Your phone number works well.',
  'setup.password': 'Password',
  'setup.confirmPassword': 'Confirm password',
  'setup.dailyRate': 'Daily feed cost per animal',
  'setup.dailyRateHint':
    'What one animal costs you per day in grass and feed. The app adds this automatically every day, so you never have to enter it. You can change it any time.',
  'setup.numberFormat': 'How should amounts be shown?',
  'setup.submit': 'Create My Account',
  'setup.alreadyDone': 'Setup is already complete.',

  // --- auth ---
  'auth.signIn': 'Sign In',
  'auth.username': 'Username',
  'auth.password': 'Password',
  'auth.submit': 'Sign In',
  'auth.invalid': 'Wrong username or password.',
  'auth.locked': 'Too many attempts. Try again in a few minutes.',
  'auth.inactive': 'This account has been turned off.',

  // --- animal fields ---
  'animal.one': 'Animal',
  'animal.many': 'Animals',
  'animal.add': 'Add Animal',
  'animal.tagNumber': 'Tag number',
  'animal.nickname': 'Name',
  'animal.nicknameHint': 'Optional. Easier to remember than a tag number.',
  'animal.sex': 'Type',
  'animal.breed': 'Breed',
  'animal.teeth': 'Age (teeth)',
  'animal.teethHint': 'Age is counted by permanent teeth, as at the mandi.',
  'animal.colour': 'Colour',
  'animal.markings': 'Markings',
  'animal.markingsHint': 'Anything that helps you recognise it later.',
  'animal.weightIn': 'Weight at purchase (kg)',
  'animal.weightOut': 'Weight at sale (kg)',
  'animal.status': 'Status',
  'animal.purchaseDate': 'Purchase date',
  'animal.purchasePrice': 'Purchase price',
  'animal.seller': 'Bought from',
  'animal.mandi': 'Mandi',
  'animal.askingPrice': 'Asking price',
  'animal.notes': 'Notes',
  'animal.photos': 'Photos & Videos',
  'animal.empty': 'No animals yet.',
  'animal.emptyHint': 'Add your first bull or cow to start tracking what it costs you.',
  'animal.daysHeld': '{days} days held',

  // --- money ---
  'money.costSoFar': 'Cost so far',
  'money.whatItHasCost': 'What it has cost you',
  'money.breakEven': 'Break-even today',
  'money.breakEvenHint': 'Sell below this and you lose money.',
  'money.perDay': 'per day',
  'money.purchasePrice': 'Purchase price',
  'money.feedCost': 'Feed',
  'money.directCosts': 'Vet & other',
  'money.sharedCosts': 'Shared costs',
  'money.dalali': 'Dalali',
  'money.dalaliHint': 'Taken out of the sale price, so break-even already allows for it.',
  'money.transport': 'Transport',
  'money.mandiFee': 'Mandi fee',
  'money.profit': 'Profit',
  'money.loss': 'Loss',
  'money.daysHeld': 'Days held',
  'money.profitPerDay': 'Profit per day',
  'money.roi': 'Return',
  'money.hidden': 'Prices are hidden',

  // --- feed (BR §6: a real bill switches the estimate off) ---
  'feed.dailyEstimate': 'Daily feed estimate',
  'feed.dailyEstimateHint': 'Used only on days no feed bill covers.',
  'feed.bought': 'Feed bought',
  'feed.wanda': 'Wanda',
  'feed.toori': 'Toori',
  'feed.kuttar': 'Kuttar',
  'feed.suppressed': 'Estimate switched off for {days} days covered by a feed bill.',

  // --- people ---
  'people.title': 'People',
  'people.munshi': 'Munshi',
  'people.add': 'Add Person',
  'people.role': 'Role',

  // --- teeth (BR §15 — age is teeth, there is no date of birth) ---
  'teeth.KHEERA': 'Kheera',
  'teeth.DANT_2': '2 Dant',
  'teeth.DANT_4': '4 Dant',
  'teeth.DANT_6': '6 Dant',
  'teeth.DANT_8': '8 Dant',

  // --- sex ---
  'sex.BULL': 'Bull',
  'sex.COW': 'Cow',
  'sex.BULL_CALF': 'Bull Calf',
  'sex.HEIFER': 'Heifer',

  // --- breeds ---
  'breed.SAHIWAL': 'Sahiwal',
  'breed.CHOLISTANI': 'Cholistani',
  'breed.NILI_RAVI': 'Nili-Ravi (Buffalo)',
  'breed.KUNDI': 'Kundi (Buffalo)',
  'breed.KANKREJ': 'Kankrej',
  'breed.RED_SINDHI': 'Red Sindhi',
  'breed.DHANNI': 'Dhanni',
  'breed.BHAGNARI': 'Bhagnari',
  'breed.DAJAL': 'Dajal',
  'breed.THARPARKAR': 'Tharparkar',
  'breed.ACHAI': 'Achai',
  'breed.ROHI': 'Rohi',
  'breed.FRIESIAN_CROSS': 'Friesian Cross',
  'breed.JERSEY_CROSS': 'Jersey Cross',
  'breed.SAHIWAL_CROSS': 'Sahiwal Cross',
  'breed.AUSTRALIAN': 'Australian / Imported',
  'breed.DESI': 'Desi',
  'breed.CROSSBREED': 'Crossbreed',
  'breed.OTHER': 'Other',

  // --- animal status ---
  'animalStatus.AVAILABLE': 'In Stock',
  'animalStatus.RESERVED': 'Reserved',
  'animalStatus.SOLD': 'Sold',
  'animalStatus.DIED': 'Died',
  'animalStatus.LOST': 'Lost',

  // --- expense categories ---
  'expenseCategory.VET_MEDICINE': 'Vet / Medicine',
  'expenseCategory.VACCINATION': 'Vaccination',
  'expenseCategory.FEED_EXTRA': 'Extra Feed (beyond daily rate)',
  'expenseCategory.FEED_WANDA': 'Wanda',
  'expenseCategory.FEED_TOORI': 'Toori',
  'expenseCategory.FEED_KUTTAR': 'Kuttar',
  'expenseCategory.FEED_BULK_OTHER': 'Other bulk feed',
  'expenseCategory.LABOUR': 'Labour / Wages',
  'expenseCategory.SHED_RENT': 'Shed Rent',
  'expenseCategory.UTILITIES': 'Utilities',
  'expenseCategory.GROOMING': 'Grooming',
  'expenseCategory.TRANSPORT': 'Transport',
  'expenseCategory.MANDI_FEE': 'Mandi Fee',
  'expenseCategory.COMMISSION_DALALI': 'Dalali',
  'expenseCategory.MISC': 'Miscellaneous',

  // --- payment methods ---
  'paymentMethod.CASH': 'Cash',
  'paymentMethod.BANK': 'Bank Transfer',
  'paymentMethod.EASYPAISA': 'Easypaisa',
  'paymentMethod.JAZZCASH': 'JazzCash',
  'paymentMethod.CHEQUE': 'Cheque',
  'paymentMethod.OTHER': 'Other',

  // --- roles ---
  'role.OWNER': 'Owner',
  'role.PARTNER': 'Partner / Manager',
  'role.STAFF': 'Staff',
  'roleDescription.OWNER':
    'Full access. Manages people, settings, backups and can delete records.',
  'roleDescription.PARTNER':
    'Sees all money and profit. Cannot manage people, settings or delete records.',
  'roleDescription.STAFF':
    'Records animals and expenses. Cannot see any prices, profit or reports.',
  'platformRole.PLATFORM_OWNER': 'Platform Owner',
  'platformRole.PLATFORM_SUPPORT': 'Platform Support',
  'platformRoleDescription.PLATFORM_OWNER':
    'Runs the platform. Plans, billing, suspensions and the danger zone.',
  'platformRoleDescription.PLATFORM_SUPPORT':
    'Reads a business to help with a problem. Cannot bill, suspend or delete.',

  // --- common ---
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.search': 'Search',
  'common.loading': 'Loading…',
  'common.required': 'Required',
  'common.optional': 'Optional',
  'common.back': 'Back',
  'common.none': '—',
  'common.denied': 'You do not have permission to view that.',
} as const satisfies Record<string, string> & Record<CodeMessageKey, string>

export type MessageKey = keyof typeof en

export type Locale = 'en'

/** Wired but not yet variable — Urdu is a later phase. */
export const LOCALE: Locale = 'en'
export const DIRECTION: 'ltr' | 'rtl' = 'ltr'

const dictionary: Record<MessageKey, string> = en

/** Placeholders are `{name}`, which survives translation into Urdu unharmed. */
const PLACEHOLDER = /\{(\w+)\}/g

export type MessageVars = Record<string, string | number>

/**
 * Look up a user-facing string.
 *
 * Typed against the dictionary, so a typo in a key is a build error rather
 * than a blank label discovered by the trader.
 */
export function t(key: MessageKey, vars?: MessageVars): string {
  const template = dictionary[key] ?? key
  if (!vars) return template
  return template.replace(PLACEHOLDER, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}
